// Terminal logging helpers — chalk for color, ora for spinners.
// All Avatar CLI output should funnel through here for consistent UX.
import chalk from "chalk";
import ora, { type Ora } from "ora";

type LogFn = (message: string) => void;

// #280: ở chế độ máy đọc (`--json`), stdout CHỈ được chứa đúng 1 dòng payload JSON
// (luật 4 của machine-readable-json-output-contract). Nhánh tự-cài GitNexus in log
// người-đọc ra stdout TRƯỚC payload ⇒ trên máy chưa có gitnexus, `doctor --json` trả
// ra thứ JSON.parse không nuốt được. Chặn tại đây thay vì đi sửa từng lời gọi log:
// mọi log tương lai nằm dưới đường `--json` cũng được bịt sẵn, không phải nhớ luật.
//
// Cờ này CHỈ bật ở nhánh `--json` ⇒ đường text của người dùng terminal không đổi
// một byte nào. Log vẫn ĐẦY ĐỦ, chỉ đổi kênh sang stderr — người chạy tay vẫn thấy.
let humanLogsGoToStderr = false;

/** Bật/tắt chế độ máy đọc: dồn mọi log người-đọc sang stderr, chừa stdout cho JSON. */
export function setMachineReadableMode(on: boolean): void {
  humanLogsGoToStderr = on;
}

/**
 * Đang ở chế độ máy đọc? Dùng cho chỗ KHÔNG ghi qua `log.*` mà vẫn chiếm stdout —
 * điển hình là `stdio: "inherit"` của tiến trình con (npm progress). Chỗ đó phải tự
 * lái stdout con sang stderr, logger không chặn hộ được.
 */
export function isMachineReadableMode(): boolean {
  return humanLogsGoToStderr;
}

/** Kênh ra của log người-đọc — stderr khi đang ở chế độ máy đọc, stdout khi không. */
function writeHumanLog(text: string): void {
  if (humanLogsGoToStderr) process.stderr.write(text);
  else process.stdout.write(text);
}

export const log: {
  info: LogFn;
  success: LogFn;
  warn: LogFn;
  error: LogFn;
  dim: LogFn;
  plain: LogFn;
} = {
  info: (m) => writeHumanLog(`${chalk.blue("ℹ")} ${m}\n`),
  success: (m) => writeHumanLog(`${chalk.green("✓")} ${m}\n`),
  warn: (m) => writeHumanLog(`${chalk.yellow("⚠")} ${m}\n`),
  error: (m) => process.stderr.write(`${chalk.red("✗")} ${m}\n`),
  dim: (m) => writeHumanLog(`${chalk.dim(m)}\n`),
  plain: (m) => writeHumanLog(`${m}\n`),
};

// Spinner wrapper — handles non-TTY by degrading to plain output.
export function spinner(text: string): Ora {
  return ora({
    text,
    spinner: "dots",
    isEnabled: process.stdout.isTTY ?? false,
  }).start();
}

// Long-running spinner với elapsed time — update text mỗi giây.
// Trả về { stop } để caller clear interval + stop spinner đúng cách.
// Format: "⠋ <prefix> (1:23)" — user biết tool alive + chạy bao lâu.
export function spinnerWithElapsed(prefix: string): {
  succeed: (text: string) => void;
  fail: (text: string) => void;
  stop: () => void;
} {
  const startMs = Date.now();
  const sp = spinner(`${prefix} (0:00)`);
  const formatElapsed = (): string => {
    const sec = Math.floor((Date.now() - startMs) / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };
  const interval = setInterval(() => {
    sp.text = `${prefix} (${formatElapsed()})`;
  }, 1000);
  return {
    succeed: (text: string) => {
      clearInterval(interval);
      sp.succeed(`${text} (${formatElapsed()})`);
    },
    fail: (text: string) => {
      clearInterval(interval);
      sp.fail(`${text} (${formatElapsed()})`);
    },
    stop: () => {
      clearInterval(interval);
      sp.stop();
    },
  };
}

// Chalk re-export so commands don't all import chalk separately.
export { chalk };
